import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, FileText, Mail, Check, X, Download, Send, Trash2, Eye, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CanoneRai = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [notes, setNotes] = useState('');
  const [sendingPec, setSendingPec] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    fetchRecords();
    fetchClients();
  }, [yearFilter]);

  const fetchRecords = async () => {
    try {
      let url = `${API}/canone-rai`;
      if (yearFilter && yearFilter !== 'all') {
        url += `?year=${yearFilter}`;
      }
      const response = await axios.get(url);
      setRecords(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei record');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleClientSearch = (term) => {
    setClientSearchTerm(term);
    if (!term.trim()) {
      setFilteredClients(clients);
      return;
    }
    const filtered = clients.filter(c => 
      `${c.last_name} ${c.first_name}`.toLowerCase().includes(term.toLowerCase()) ||
      c.fiscal_code?.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredClients(filtered);
  };

  const handleCreate = async () => {
    if (!selectedClientId) {
      toast.error('Seleziona un cliente');
      return;
    }

    try {
      await axios.post(`${API}/canone-rai`, {
        client_id: selectedClientId,
        year: selectedYear,
        notes: notes
      });
      toast.success('Record creato con successo');
      setDialogOpen(false);
      resetForm();
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const resetForm = () => {
    setSelectedClientId('');
    setSelectedYear(new Date().getFullYear());
    setNotes('');
    setClientSearchTerm('');
    setFilteredClients(clients);
  };

  const handleUpdatePayment = async (recordId, paymentDone) => {
    try {
      await axios.put(`${API}/canone-rai/${recordId}`, { payment_done: paymentDone });
      toast.success('Stato pagamento aggiornato');
      fetchRecords();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleGeneratePdf = async (recordId) => {
    try {
      const response = await axios.get(`${API}/canone-rai/${recordId}/generate-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'CANONE_RAI.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF generato e scaricato');
      fetchRecords();
    } catch (error) {
      toast.error('Errore nella generazione del PDF');
    }
  };

  const handleSendPec = async (recordId) => {
    setSendingPec(recordId);
    try {
      const response = await axios.post(`${API}/canone-rai/${recordId}/send-pec`);
      toast.success(response.data.message || 'PEC inviata con successo');
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della PEC');
    } finally {
      setSendingPec(null);
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Eliminare questo record?')) return;
    try {
      await axios.delete(`${API}/canone-rai/${recordId}`);
      toast.success('Record eliminato');
      fetchRecords();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  // Sort function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Filter and sort records
  const filteredRecords = records
    .filter(r => 
      r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.client_fiscal_code?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle date sorting
      if (key === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      // Handle boolean sorting (payment_done, pec_sent)
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      }
      // Handle year sorting
      if (key === 'year') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="canone-rai-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Canone RAI - Esenzioni
          </h1>
          <p className="text-slate-600 mt-1">Gestione richieste esenzione Canone RAI</p>
        </div>
        <Button 
          className="bg-slate-900 hover:bg-slate-800 text-white" 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          data-testid="new-canone-rai-btn"
        >
          <Plus className="w-4 h-4 mr-2" />Nuova Richiesta
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome cliente o codice fiscale..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-canone-rai"
              />
            </div>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-40" data-testid="year-filter">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli anni</SelectItem>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Nessun record trovato</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Codice Fiscale</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('year')}
                    >
                      <div className="flex items-center">
                        Anno
                        {getSortIcon('year')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center">
                        Data Inserimento
                        {getSortIcon('created_at')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('payment_done')}
                    >
                      <div className="flex items-center">
                        Pagamento
                        {getSortIcon('payment_done')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('pec_sent')}
                    >
                      <div className="flex items-center">
                        PEC Inviata
                        {getSortIcon('pec_sent')}
                      </div>
                    </TableHead>
                    <TableHead>PDF</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id} data-testid={`canone-rai-row-${record.id}`}>
                      <TableCell className="font-medium">{record.client_name}</TableCell>
                      <TableCell className="font-mono text-sm">{record.client_fiscal_code}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{record.year}</Badge>
                      </TableCell>
                      <TableCell>{record.insertion_date ? new Date(record.insertion_date).toLocaleDateString('it-IT') : '-'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdatePayment(record.id, !record.payment_done)}
                          className={record.payment_done ? 'text-green-600' : 'text-slate-400'}
                        >
                          {record.payment_done ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                          <span className="ml-1">{record.payment_done ? 'Sì' : 'No'}</span>
                        </Button>
                      </TableCell>
                      <TableCell>
                        {record.pec_sent ? (
                          <Badge className="bg-green-100 text-green-700">
                            <Mail className="w-3 h-3 mr-1" />
                            Inviata
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">
                            Non inviata
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {record.pdf_generated ? (
                          <Badge className="bg-blue-100 text-blue-700">
                            <FileText className="w-3 h-3 mr-1" />
                            Generato
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">-</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleGeneratePdf(record.id)}>
                              <Download className="w-4 h-4 mr-2" />Genera PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleSendPec(record.id)}
                              disabled={sendingPec === record.id}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              {sendingPec === record.id ? 'Invio in corso...' : 'Invia PEC'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(record.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Record Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuova Richiesta Esenzione Canone RAI</DialogTitle>
            <DialogDescription>
              Seleziona il cliente e l'anno per la richiesta di esenzione
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Client Search */}
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Input
                placeholder="Cerca cliente per nome o codice fiscale..."
                value={clientSearchTerm}
                onChange={(e) => handleClientSearch(e.target.value)}
                data-testid="client-search-input"
              />
              
              {/* Client List */}
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {filteredClients.length === 0 ? (
                  <div className="p-3 text-center text-slate-500 text-sm">
                    Nessun cliente trovato
                  </div>
                ) : (
                  filteredClients.slice(0, 10).map((client) => (
                    <div
                      key={client.id}
                      onClick={() => {
                        setSelectedClientId(client.id);
                        setClientSearchTerm(`${client.last_name} ${client.first_name}`);
                      }}
                      className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${
                        selectedClientId === client.id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                      data-testid={`client-option-${client.id}`}
                    >
                      <div className="font-medium">{client.last_name} {client.first_name}</div>
                      <div className="text-sm text-slate-500 font-mono">{client.fiscal_code}</div>
                    </div>
                  ))
                )}
              </div>
              {selectedClientId && (
                <p className="text-sm text-green-600">
                  Cliente selezionato
                </p>
              )}
            </div>

            {/* Year Selection */}
            <div className="space-y-2">
              <Label>Anno Esenzione *</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleCreate}
                className="bg-slate-900 hover:bg-slate-800"
                disabled={!selectedClientId}
                data-testid="create-canone-rai-btn"
              >
                Crea Richiesta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CanoneRai;
